// Circle size: 100px on md/xl. Connector must align to circle center (50px from top).
const CIRCLE_SIZE_MD = 100; // px

const StepItem = ({ step, icon: Icon, label, description }) => {
    return (
        <div className="flex flex-row md:flex-col items-center group">
            {/* Circle */}
            <div className="relative flex-shrink-0">
                {/* Outer glow */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 blur-sm opacity-40 group-hover:opacity-60 transition-opacity duration-300"></div>

                <div className="relative w-[65px] h-[65px] md:w-[126px] md:h-[126px] rounded-full bg-gradient-to-br from-purple-600 to-blue-600 p-[2px]">
                    <div className="w-full h-full rounded-full bg-[#1a1a2e] flex items-center justify-center group-hover:bg-[#0f0f1e] transition-colors duration-300">
                        <Icon className="w-[26px] h-[26px] md:w-[44px] md:h-[44px] text-white" strokeWidth={1.5} />
                    </div>
                </div>

                {/* Step Number Badge */}
                <div className="absolute -top-2 -right-2 md:-top-1 md:-right-1 w-7 h-7 md:w-11 md:h-11 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 text-white flex items-center justify-center font-bold text-[10px] md:text-sm shadow-lg">
                    {String(step).padStart(2, '0')}
                </div>
            </div>

            {/* Label */}
            <div className="ml-4 md:ml-0 md:mt-6 md:text-center md:max-w-[140px]">
                <p className="text-sm md:text-base font-semibold text-white mb-1 md:mb-1.5">{label}</p>
                {description && (
                    <p className="text-xs text-gray-300 leading-snug">{description}</p>
                )}
            </div>
        </div>
    );
};

// Connector sits at exactly the circle's vertical center (50px = half of 100px circle)
const HorizontalConnector = () => (
    <div
        className="hidden md:flex flex-shrink-0 flex-col items-center gap-1.5 px-2 xl:px-4"
        style={{ marginTop: `${CIRCLE_SIZE_MD / 2}px`, transform: 'translateY(-50%)' }}
    >
        <div className="w-10 xl:w-16 h-[2px] bg-gradient-to-r from-purple-600 to-blue-600 opacity-30"></div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="opacity-50">
            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="url(#gradH)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <defs>
                <linearGradient id="gradH" x1="5" y1="12" x2="19" y2="12">
                    <stop offset="0%" stopColor="#9333ea"/>
                    <stop offset="100%" stopColor="#2563eb"/>
                </linearGradient>
            </defs>
        </svg>
        <div className="w-10 xl:w-16 h-[2px] bg-gradient-to-r from-purple-600 to-blue-600 opacity-30"></div>
    </div>
);

const VerticalConnector = () => (
    <div className="flex md:hidden flex-col items-center my-1 ml-[32px]">
        <div className="w-[2px] h-5 bg-gradient-to-b from-purple-600 to-blue-600 opacity-30"></div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="opacity-50">
            <path d="M12 5V19M12 19L5 12M12 19L19 12" stroke="url(#gradV)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <defs>
                <linearGradient id="gradV" x1="12" y1="5" x2="12" y2="19">
                    <stop offset="0%" stopColor="#9333ea"/>
                    <stop offset="100%" stopColor="#2563eb"/>
                </linearGradient>
            </defs>
        </svg>
        <div className="w-[2px] h-5 bg-gradient-to-b from-purple-600 to-blue-600 opacity-30"></div>
    </div>
);

const StepsProcess = ({ steps }) => {
    return (
        <>
            {/* Mobile: vertical list */}
            <div className="flex md:hidden flex-col px-4">
                {steps.map((step, index) => (
                    <div key={index}>
                        <StepItem
                            step={index + 1}
                            icon={step.icon}
                            label={step.label}
                            description={step.description}
                        />
                        {index !== steps.length - 1 && <VerticalConnector />}
                    </div>
                ))}
            </div>

            {/* Desktop/XL: single row, connector pinned to circle center */}
            <div className="hidden md:flex flex-nowrap items-start justify-center w-full px-4 xl:px-8">
                {steps.map((step, index) => (
                    <div key={index} className="flex items-start">
                        <StepItem
                            step={index + 1}
                            icon={step.icon}
                            label={step.label}
                            description={step.description}
                        />
                        {index !== steps.length - 1 && <HorizontalConnector />}
                    </div>
                ))}
            </div>
        </>
    );
};

export default StepsProcess;